<template>
  <div class="prep-budget-table">
    <div class="prep-budget-save">
      <b-button
        v-if="canManageImportClear"
        class="prep-action-btn prep-action-btn-import"
        variant="outline-primary"
        size="sm"
        :disabled="loading || saving || autosaving || importing"
        @click="openExcelImportModal"
      >
        Importálás
      </b-button>
      <b-button
        v-if="canManageImportClear"
        class="prep-action-btn prep-action-btn-clear"
        variant="outline-danger"
        size="sm"
        :disabled="loading || saving || autosaving || importing || clearing"
        @click="openClearModal"
      >
        Ürítés
      </b-button>
      <b-button
        v-if="isSubgroupCategory"
        class="prep-action-btn prep-action-btn-assign"
        variant="outline-primary"
        size="sm"
        :disabled="loading || saving || autosaving"
        @click="openAssignModal"
      >
        Hozzá rendelés
      </b-button>
      <b-button
        v-if="isSubgroupCategory"
        class="prep-action-btn prep-action-btn-new"
        variant="outline-primary"
        size="sm"
        :disabled="loading || saving || autosaving"
        @click="openCreateModal"
      >
        Új
      </b-button>
      <b-button
        class="prep-action-btn prep-action-btn-refresh"
        :variant="hasPersistedEntries ? 'primary' : 'outline-secondary'"
        size="sm"
        :disabled="loading || saving || !hasPersistedEntries"
        @click="refreshItems"
      >
        Frissítés
      </b-button>
      <b-button
        class="prep-action-btn prep-action-btn-save"
        :variant="hasPersistedEntries ? 'outline-secondary' : 'primary'"
        size="sm"
        :disabled="saving || loading || hasPersistedEntries"
        @click="saveItems"
      >
        {{ saving ? 'Mentés...' : 'Mentés' }}
      </b-button>
    </div>
    <b-modal
      v-model="assignModalOpen"
      title="Tételek hozzárendelése"
      centered
      size="lg"
      @ok.prevent="confirmAssignSelection"
      @shown="focusAssignList"
    >
      <div
        v-if="assignLoading"
        class="prep-detail-placeholder"
      >
        Betöltés...
      </div>
      <div v-else>
        <div
          ref="assignList"
          class="prep-assign-list"
          tabindex="0"
          @keydown.enter.prevent="confirmAssignSelection"
        >
          <button
            v-for="row in assignCandidates"
            :key="`candidate-${row.id}`"
            type="button"
            class="prep-assign-item"
            :class="{ selected: selectedCandidateIds.includes(row.id) }"
            @click="onAssignCandidateClick(row, $event)"
          >
            {{ row.item_text }}
          </button>
        </div>
        <div class="text-muted mt-50">
          Tipp: `Ctrl + kattintás` többszörös kijelöléshez, majd `Enter`.
        </div>
      </div>
    </b-modal>
    <b-modal
      v-model="createModalOpen"
      title="Új tétel"
      centered
      size="md"
      @ok.prevent="createNewItem"
    >
      <b-form-input
        v-model="newItemText"
        placeholder="Új tétel szövege"
      />
    </b-modal>
    <b-modal
      v-model="excelImportModalOpen"
      title="Excel import"
      centered
      size="md"
      @ok.prevent="confirmExcelImport"
      @hidden="resetExcelImportDraft"
    >
      <b-form-file
        v-model="excelImportFile"
        accept=".xlsx,.xls"
      />
      <small class="text-muted d-block mt-50">Tallózd be az Excel fájlt, majd OK.</small>
      <template #modal-footer="{ ok, cancel }">
        <div class="w-100 d-flex justify-content-end gap-50">
          <b-button
            variant="outline-secondary"
            :disabled="importing"
            @click="cancel()"
          >
            Cancel
          </b-button>
          <b-button
            variant="primary"
            :disabled="importing || !excelImportFile"
            @click="ok()"
          >
            {{ importing ? 'Importálás...' : 'OK' }}
          </b-button>
        </div>
      </template>
    </b-modal>
    <b-modal
      v-model="excelPreviewModalOpen"
      title="Import előnézet és jóváhagyás"
      centered
      size="lg"
      @ok.prevent="confirmExcelPreview"
    >
      <div
        v-if="!previewTargets.length"
        class="text-muted"
      >
        Nincs előnézeti adat.
      </div>
      <div
        v-else
        class="d-flex flex-column gap-75"
      >
        <div
          v-for="(target, idx) in previewTargets"
          :key="`preview-target-${idx}`"
          class="prep-assign-item"
        >
          <div><strong>Menü:</strong> {{ target.menu || '-' }}</div>
          <div><strong>Lap:</strong> {{ target.sheet || '-' }}</div>
          <div><strong>Státusz:</strong> {{ target.status || '-' }}</div>

          <div
            v-if="target.status !== 'missing_sheet'"
            class="mt-50 d-flex flex-column gap-50"
          >
            <div
              v-for="field in requiredFieldsForTarget(target)"
              :key="`preview-${idx}-${field}`"
            >
              <label class="d-block mb-25">
                {{ previewFieldLabel(field) }}
              </label>
              <b-form-select
                v-model="excelPreviewSelections[previewTargetKey(target, idx)][field]"
                :options="previewFieldOptions(target, field)"
              />
            </div>
          </div>

          <div
            v-if="target.status === 'missing_sheet'"
            class="mt-50 text-warning"
          >
            Ehhez a menühöz nem talált lapot az Excelben.
          </div>
          <div
            v-if="target.status === 'ready'"
            class="mt-50"
          >
            <div class="text-muted mb-25">
              Felismert oszlopok:
            </div>
            <div
              v-for="(resolved, rIdx) in previewResolvedRows(target)"
              :key="`ready-${idx}-${rIdx}`"
              class="text-muted"
            >
              {{ resolved.label }}: <strong>{{ resolved.column }}</strong>
            </div>
          </div>
        </div>
      </div>
      <small class="text-muted d-block mt-50">
        Az OK gomb a jóváhagyott oszlopokkal indítja a tényleges importálást.
      </small>
      <template #modal-footer="{ ok, cancel }">
        <div class="w-100 d-flex justify-content-end gap-50">
          <b-button
            variant="outline-secondary"
            :disabled="importing"
            @click="cancel()"
          >
            Mégse
          </b-button>
          <b-button
            variant="primary"
            :disabled="importing || !canSubmitPreview"
            @click="ok()"
          >
            {{ importing ? 'Importálás...' : 'Jóváhagyás és import' }}
          </b-button>
        </div>
      </template>
    </b-modal>
    <b-modal
      v-model="excelResolutionModalOpen"
      title="Import oszlop kiválasztás"
      centered
      size="md"
      @ok.prevent="confirmExcelResolution"
    >
      <div
        v-if="excelImportResolution"
        class="mb-50"
      >
        <div><strong>Menü:</strong> {{ excelImportResolution.menu || '-' }}</div>
        <div><strong>Lap:</strong> {{ excelImportResolution.sheet || '-' }}</div>
        <div class="mt-50">
          {{ excelImportResolution.message || 'Válaszd ki a megfelelő oszlopot.' }}
        </div>
      </div>
      <div
        v-if="resolutionUnresolvedFields.length"
        class="d-flex flex-column gap-50"
      >
        <div
          v-for="field in resolutionUnresolvedFields"
          :key="`resolve-${field.field}`"
        >
          <label class="d-block mb-25">
            {{ field.field_label || field.field }}
          </label>
          <b-form-select
            v-model="excelResolutionSelections[field.field]"
            :options="fieldOptions(field)"
          />
        </div>
      </div>
      <small class="text-muted d-block mt-50">
        Az OK gombbal újrafut az import a kiválasztott oszloppal.
      </small>
      <template #modal-footer="{ ok, cancel }">
        <div class="w-100 d-flex justify-content-end gap-50">
          <b-button
            variant="outline-secondary"
            :disabled="importing"
            @click="cancel()"
          >
            Mégse
          </b-button>
          <b-button
            variant="primary"
            :disabled="importing || !canSubmitResolution"
            @click="ok()"
          >
            {{ importing ? 'Importálás...' : 'OK' }}
          </b-button>
        </div>
      </template>
    </b-modal>
    <b-modal
      v-model="clearModalOpen"
      title="Kártya adatok ürítése"
      centered
      size="md"
      @ok.prevent="confirmClearOfferData"
      @hidden="resetClearDraft"
    >
      <div class="mb-75">
        Mit töröljünk az aktuális kártyáról?
      </div>
      <b-form-checkbox
        v-model="clearEntries"
      >
        Mentett költségvetési adatok (budget_offer_entries)
      </b-form-checkbox>
      <b-form-checkbox
        v-model="clearMenu"
        class="mt-50"
      >
        Menü felépítés (kártya menübeállítás)
      </b-form-checkbox>
      <small class="text-muted d-block mt-75">
        A törlés csak az aktuális kártyára vonatkozik.
      </small>
      <template #modal-footer="{ ok, cancel }">
        <div class="w-100 d-flex justify-content-end gap-50">
          <b-button
            variant="outline-secondary"
            :disabled="clearing"
            @click="cancel()"
          >
            Cancel
          </b-button>
          <b-button
            variant="danger"
            :disabled="clearing || (!clearEntries && !clearMenu)"
            @click="ok()"
          >
            {{ clearing ? 'Törlés...' : 'Törlés' }}
          </b-button>
        </div>
      </template>
    </b-modal>
    <b-modal
      v-model="importReportModalOpen"
      title="Import validációs riport"
      centered
      size="lg"
      ok-only
      ok-title="Bezár"
    >
      <div v-if="importReport">
        <div class="mb-75">
          Feldolgozott lapok: <strong>{{ importReportSummary.processed_sheets }}</strong>,
          új tételek: <strong>{{ importReportSummary.new_items }}</strong>,
          mentett sorok: <strong>{{ importReportSummary.saved_rows }}</strong>
        </div>
        <div class="mb-75 text-muted">
          Egyezőség: rendben <strong>{{ importReportSummary.consistency.ok }}</strong>,
          eltérés <strong>{{ importReportSummary.consistency.mismatch }}</strong>,
          kihagyott <strong>{{ importReportSummary.consistency.skipped }}</strong>
        </div>
        <div
          v-if="Array.isArray(importReport.warnings) && importReport.warnings.length"
          class="mb-75 text-warning"
        >
          <div
            v-for="(warning, idx) in importReport.warnings"
            :key="`warn-${idx}`"
          >
            - {{ warning }}
          </div>
        </div>
        <div class="prep-assign-list">
          <div
            v-for="(row, idx) in importReportRows"
            :key="`report-${idx}`"
            class="prep-assign-item"
          >
            <strong>{{ row.menu }}</strong> |
            kategória: {{ row.category }} |
            lap: {{ row.sheet || '-' }} |
            státusz: {{ row.status }} |
            új tétel: {{ row.new_items || 0 }} |
            mentett sor: {{ row.saved_rows || 0 }} |
            egyezőség: {{ row.consistency_status || '-' }}
            <template v-if="row.consistency_status === 'mismatch'">
              | anyag diff: {{ formatFt(row.material_diff || 0) }}
              | díj diff: {{ formatFt(row.fee_diff || 0) }}
            </template>
          </div>
        </div>
      </div>
      <div v-else>
        Nincs riport adat.
      </div>
    </b-modal>
    <div class="prep-budget-table-scroll">
      <div
        v-if="loading"
        class="prep-detail-placeholder"
      >
        Betöltés...
      </div>
      <div
        v-else-if="error"
        class="prep-detail-placeholder"
      >
        {{ error }}
      </div>
      <div
        v-else
        class="prep-budget-item-list"
      >
        <budget-item-card
          v-for="item in rowsForDisplay"
          :key="item.id"
          :item-id="item.id"
          :item-text="item.itemText"
          :value="item"
          :summary-mode="isSummaryCategory"
          :hide-material-columns="isJarulekosCategory"
          :show-planned-expense-control="!isSummaryCategory"
          :edit-mode="editMode"
          :show-actions="isSubgroupCategory"
          @change="updateRow"
          @edit-item-text="onEditItemText"
          @unlink-subgroup="onUnlinkSubgroup"
        />
        <div
          v-if="!rowsForDisplay.length"
          class="prep-detail-placeholder"
        >
          Nincs megjeleníthető tétel.
        </div>
      </div>
    </div>
    <div
      v-if="rowsForDisplay.length"
      class="prep-budget-summary-bar"
    >
      <div class="prep-budget-summary-title">
        Munkanem összesen NETTÓ ár:
      </div>
      <div class="prep-budget-summary-items">
        <div class="prep-budget-summary-item">
          <div class="prep-budget-summary-item-label-box">
            <span class="prep-budget-summary-item-label">Anyag összesen</span>
          </div>
          <div class="prep-budget-summary-item-value-box">
            <strong class="prep-budget-summary-item-value">{{ formatFt(totalMaterial) }}</strong>
          </div>
        </div>
        <div class="prep-budget-summary-item">
          <div class="prep-budget-summary-item-label-box">
            <span class="prep-budget-summary-item-label">Díj összesen</span>
          </div>
          <div class="prep-budget-summary-item-value-box">
            <strong class="prep-budget-summary-item-value">{{ formatFt(totalFee) }}</strong>
          </div>
        </div>
        <div class="prep-budget-summary-item total">
          <div class="prep-budget-summary-item-label-box">
            <span class="prep-budget-summary-item-label">Nettó összesen</span>
          </div>
          <div class="prep-budget-summary-item-value-box">
            <strong class="prep-budget-summary-item-value">{{ formatFt(totalNet) }}</strong>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import {
  BButton, BModal, BFormInput, BFormFile, BFormCheckbox, BFormSelect,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import BudgetItemCard from './BudgetItemCard.vue'

export default {
  name: 'BudgetDeepFoundationList',
  components: {
    BudgetItemCard,
    BButton,
    BModal,
    BFormInput,
    BFormFile,
    BFormCheckbox,
    BFormSelect,
  },
  props: {
    endpoint: {
      type: String,
      default: 'budget/items/mely-alapozas',
    },
    categoryKey: {
      type: String,
      default: '',
    },
    subgroupKey: {
      type: String,
      default: '',
    },
    subgroupOptions: {
      type: Array,
      default: () => [],
    },
    editMode: {
      type: String,
      default: 'full',
    },
    showPositiveQtyOnly: {
      type: Boolean,
      default: false,
    },
    offerType: {
      type: String,
      default: '',
    },
    offerId: {
      type: [Number, String],
      default: null,
    },
  },
  data() {
    return {
      rows: [],
      loading: false,
      error: '',
      saving: false,
      autosaving: false,
      hasChanges: false,
      importing: false,
      clearing: false,
      excelImportModalOpen: false,
      excelImportFile: null,
      excelImportPreview: null,
      excelPreviewModalOpen: false,
      excelPreviewSelections: {},
      excelImportResolution: null,
      excelResolutionModalOpen: false,
      excelResolutionSelections: {},
      excelImportColumnOverrides: {},
      importReportModalOpen: false,
      importReport: null,
      clearModalOpen: false,
      clearEntries: true,
      clearMenu: false,
      hasPersistedEntries: false,
      autosaveTimer: null,
      pendingAutosaveContext: null,
      pendingAutosaveEntries: null,
      correctionBaseRows: null,
      assignModalOpen: false,
      assignLoading: false,
      assignCandidates: [],
      selectedCandidateIds: [],
      createModalOpen: false,
      newItemText: '',
    }
  },
  computed: {
    canManageImportClear() {
      const user = JSON.parse(localStorage.getItem('user') || '{}')
      const roles = Array.isArray(user?.role) ? user.role : []
      return roles.includes('prep_view_all')
    },
    isSummaryCategory() {
      return this.resolvedCategoryKey() === 'osszesito'
    },
    isSubgroupCategory() {
      return !!(this.subgroupKey && !this.isSummaryCategory)
    },
    isJarulekosCategory() {
      return this.resolvedCategoryKey() === 'jarulekos-koltsegek'
    },
    rowsForDisplay() {
      const { rows } = this
      if (!this.showPositiveQtyOnly) return rows
      return rows.filter(row => (Number(row.qty) || 0) > 0)
    },
    totalMaterial() {
      return this.rowsForDisplay.reduce((sum, row) => sum + (Number(row.material_total) || 0), 0)
    },
    totalFee() {
      return this.rowsForDisplay.reduce((sum, row) => sum + (Number(row.fee_total) || 0), 0)
    },
    totalNet() {
      return this.totalMaterial + this.totalFee
    },
    resolutionFieldOptions() {
      const columns = Array.isArray(this.excelImportResolution?.columns) ? this.excelImportResolution.columns : []
      return columns.map(col => ({
        value: Number(col.index),
        text: `${col.letter || ''} - ${col.label || ''}`,
      }))
    },
    resolutionUnresolvedFields() {
      const fields = this.excelImportResolution?.unresolved_fields
      if (Array.isArray(fields) && fields.length) return fields
      const field = this.excelImportResolution?.field
      if (!field) return []
      return [{
        field,
        field_label: this.excelImportResolution?.field_label || field,
        candidates: Array.isArray(this.excelImportResolution?.candidates) ? this.excelImportResolution.candidates : [],
      }]
    },
    previewTargets() {
      const targets = this.excelImportPreview?.targets
      return Array.isArray(targets) ? targets : []
    },
    canSubmitPreview() {
      if (!this.previewTargets.length) return false
      return this.previewTargets.every((target, idx) => {
        if (target?.status === 'missing_sheet') return true
        const required = this.requiredFieldsForTarget(target)
        if (!required.length) return true
        const selectedMap = this.excelPreviewSelections[this.previewTargetKey(target, idx)] || {}
        return required.every(field => {
          const value = Number(selectedMap[field] || 0)
          return Number.isFinite(value) && value > 0
        })
      })
    },
    canSubmitResolution() {
      if (!this.resolutionUnresolvedFields.length) return false
      return this.resolutionUnresolvedFields.every(field => {
        const value = Number(this.excelResolutionSelections[field.field] || 0)
        return Number.isFinite(value) && value > 0
      })
    },
    importReportRows() {
      const rows = this.importReport?.targets
      return Array.isArray(rows) ? rows : []
    },
    importReportSummary() {
      const summary = this.importReport && this.importReport.summary ? this.importReport.summary : {}
      return {
        processed_sheets: Number(summary.processed_sheets || 0),
        new_items: Number(summary.new_items || 0),
        saved_rows: Number(summary.saved_rows || 0),
        consistency: {
          ok: Number(summary?.consistency?.ok || 0),
          mismatch: Number(summary?.consistency?.mismatch || 0),
          skipped: Number(summary?.consistency?.skipped || 0),
        },
      }
    },
  },
  watch: {
    endpoint() {
      this.flushAutoPersist()
      this.fetchItems()
    },
    categoryKey() {
      this.flushAutoPersist()
      this.fetchItems()
    },
    subgroupKey() {
      this.flushAutoPersist()
      this.fetchItems()
    },
    offerType() {
      this.flushAutoPersist()
      this.fetchItems()
    },
    offerId() {
      this.flushAutoPersist()
      this.fetchItems()
    },
  },
  mounted() {
    this.fetchItems()
  },
  beforeDestroy() {
    this.flushAutoPersist()
  },
  methods: {
    openAssignModal() {
      const category = this.resolvedCategoryKey()
      if (!category) return
      this.assignModalOpen = true
      this.assignLoading = true
      this.selectedCandidateIds = []
      const token = localStorage.getItem('token')
      const loadCandidates = async () => {
        const first = await axios({
          method: 'get',
          url: 'budget/subgroup-mapping/items',
          params: {
            category,
            unassigned_only: 1,
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
        const firstRows = Array.isArray(first.data?.rows) ? first.data.rows : []
        if (firstRows.length) return firstRows

        const fallback = await axios({
          method: 'get',
          url: 'budget/subgroup-mapping/items',
          params: {
            category,
            unassigned_only: 0,
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
        return Array.isArray(fallback.data?.rows) ? fallback.data.rows : []
      }

      loadCandidates().then(rows => {
        this.assignCandidates = rows.map(row => ({
          id: row.id,
          item_text: row.item_text || '',
        }))
      }).catch(() => {
        this.assignCandidates = []
        this.notify('Hozzárendelhető tételek betöltése sikertelen', 'danger', 'XOctagonIcon')
      }).finally(() => {
        this.assignLoading = false
      })
    },
    focusAssignList() {
      this.$nextTick(() => {
        const el = this.$refs.assignList
        if (el && typeof el.focus === 'function') el.focus()
      })
    },
    onAssignCandidateClick(row, event) {
      const itemId = Number(row.id)
      const isMulti = !!(event && (event.ctrlKey || event.metaKey))
      if (isMulti) {
        if (this.selectedCandidateIds.includes(itemId)) {
          this.selectedCandidateIds = this.selectedCandidateIds.filter(id => id !== itemId)
        } else {
          this.selectedCandidateIds = [...this.selectedCandidateIds, itemId]
        }
        return
      }
      this.assignItems([itemId])
    },
    confirmAssignSelection() {
      if (!this.selectedCandidateIds.length) return
      this.assignItems(this.selectedCandidateIds)
    },
    assignItems(itemIds) {
      const category = this.resolvedCategoryKey()
      if (!category || !this.subgroupKey || !itemIds.length) return
      axios({
        method: 'post',
        url: 'budget/subgroup-mapping/assign',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        data: {
          category,
          subgroup_key: this.subgroupKey,
          item_ids: itemIds,
        },
      }).then(() => {
        this.notify('Tételek hozzárendelve', 'success', 'CheckIcon')
        this.assignModalOpen = false
        this.selectedCandidateIds = []
        this.fetchItems(false)
      }).catch(() => {
        this.notify('Tétel hozzárendelés sikertelen', 'danger', 'XOctagonIcon')
      })
    },
    openCreateModal() {
      this.newItemText = ''
      this.createModalOpen = true
    },
    createNewItem() {
      const category = this.resolvedCategoryKey()
      const text = String(this.newItemText || '').trim()
      if (!category || !this.subgroupKey || !text) return
      axios({
        method: 'post',
        url: 'budget/subgroup-mapping/create-item',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        data: {
          category,
          subgroup_key: this.subgroupKey,
          item_text: text,
        },
      }).then(() => {
        this.notify('Új tétel létrehozva', 'success', 'CheckIcon')
        this.createModalOpen = false
        this.fetchItems(false)
      }).catch(() => {
        this.notify('Új tétel létrehozása sikertelen', 'danger', 'XOctagonIcon')
      })
    },
    onEditItemText(payload) {
      const category = this.resolvedCategoryKey()
      const itemId = Number(payload && payload.item_id)
      const currentText = String((payload && payload.item_text) || '')
      if (!category || !itemId) return
      const nextText = window.prompt('Tétel szövege:', currentText)
      if (nextText === null) return
      const clean = String(nextText).trim()
      if (!clean) return
      axios({
        method: 'post',
        url: 'budget/subgroup-mapping/update-item-text',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        data: {
          category,
          item_id: itemId,
          item_text: clean,
        },
      }).then(() => {
        this.notify('Tétel szöveg frissítve', 'success', 'CheckIcon')
        this.fetchItems(false)
      }).catch(() => {
        this.notify('Tétel szöveg frissítése sikertelen', 'danger', 'XOctagonIcon')
      })
    },
    onUnlinkSubgroup(payload) {
      const category = this.resolvedCategoryKey()
      const itemId = Number(payload && payload.item_id)
      if (!category || !itemId) return
      axios({
        method: 'post',
        url: 'budget/subgroup-mapping/unlink-item',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        data: {
          category,
          item_id: itemId,
        },
      }).then(() => {
        this.notify('Almenü kapcsolat törölve', 'success', 'CheckIcon')
        this.fetchItems(false)
      }).catch(() => {
        this.notify('Almenü kapcsolat törlése sikertelen', 'danger', 'XOctagonIcon')
      })
    },
    normalizeText(value) {
      return String(value || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
    },
    formatFt(value) {
      const number = Math.round(Number(value || 0))
      return `${number.toLocaleString('hu-HU', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      })} Ft`
    },
    notify(title, variant = 'success', icon = 'CheckIcon') {
      this.$toast({
        component: ToastificationContent,
        props: {
          title,
          icon,
          variant,
        },
      })
    },
    openExcelImportModal() {
      if (!this.canManageImportClear) return
      this.excelImportModalOpen = true
    },
    resetExcelImportDraft() {
      if (this.excelResolutionModalOpen || this.excelPreviewModalOpen) return
      this.excelImportFile = null
      this.importing = false
      this.excelImportPreview = null
      this.excelPreviewSelections = {}
      this.excelImportResolution = null
      this.excelResolutionSelections = {}
      this.excelImportColumnOverrides = {}
    },
    confirmExcelImport() {
      this.runExcelImport({ previewOnly: true })
    },
    runExcelImport({ previewOnly = false } = {}) {
      if (!this.canManageImportClear) return
      const file = this.excelImportFile
      const offerType = String(this.offerType || '')
      const offerId = Number(this.offerId || 0)
      if (!file || !offerType || !offerId) return

      const formData = new FormData()
      formData.append('offer_type', offerType)
      formData.append('offer_id', String(offerId))
      formData.append('file', file)
      formData.append('preview_only', previewOnly ? '1' : '0')
      formData.append('column_overrides', JSON.stringify(this.excelImportColumnOverrides || {}))

      this.importing = true
      axios({
        method: 'post',
        url: 'budget/offer/import-xlsx',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'multipart/form-data',
        },
        data: formData,
      }).then(response => {
        if (previewOnly) {
          this.excelImportPreview = response?.data?.preview || null
          this.hydratePreviewSelections()
          this.excelImportModalOpen = false
          this.excelPreviewModalOpen = true
          this.notify('Import előnézet betöltve', 'success', 'CheckIcon')
          return
        }
        this.notify('Excel import sikeres', 'success', 'CheckIcon')
        this.excelImportModalOpen = false
        this.excelPreviewModalOpen = false
        this.excelResolutionModalOpen = false
        this.excelImportPreview = null
        this.excelPreviewSelections = {}
        this.excelImportResolution = null
        this.excelResolutionSelections = {}
        this.excelImportColumnOverrides = {}
        this.importReport = response?.data?.report || null
        this.importReportModalOpen = !!this.importReport
        this.fetchItems(false)
      }).catch(error => {
        if (error?.response?.status === 409 && error?.response?.data?.resolution) {
          const { resolution } = error.response.data
          this.excelImportResolution = resolution
          this.importReport = error?.response?.data?.report || this.importReport
          const defaults = {}
          this.resolutionUnresolvedFields.forEach(field => {
            let firstCandidate = null
            if (Array.isArray(field?.candidates) && field.candidates.length) {
              firstCandidate = Number(field.candidates[0])
            } else if (this.resolutionFieldOptions.length) {
              firstCandidate = Number(this.resolutionFieldOptions[0].value)
            }
            if (Number.isFinite(firstCandidate) && firstCandidate > 0) {
              defaults[field.field] = firstCandidate
            }
          })
          this.excelResolutionSelections = defaults
          this.excelPreviewModalOpen = false
          this.excelResolutionModalOpen = true
          this.notify('Importálás döntést kér', 'warning', 'AlertCircleIcon')
          return
        }
        const backendMessage = error?.response?.data?.message
        const backendOutput = error?.response?.data?.output
        this.importReport = error?.response?.data?.report || this.importReport
        if (this.importReport) this.importReportModalOpen = true
        const detail = [backendMessage, backendOutput]
          .filter(part => part && String(part).trim() !== '')
          .join(' | ')
        this.notify(
          detail ? `Excel import sikertelen: ${detail}` : 'Excel import sikertelen',
          'danger',
          'XOctagonIcon',
        )
      }).finally(() => {
        this.importing = false
      })
    },
    confirmExcelResolution() {
      const resolution = this.excelImportResolution || {}
      const sheet = String(resolution.sheet || '')
      if (!sheet || !this.canSubmitResolution) return
      const existing = this.excelImportColumnOverrides[sheet] || {}
      const mapped = {}
      this.resolutionUnresolvedFields.forEach(field => {
        const selected = Number(this.excelResolutionSelections[field.field] || 0)
        if (selected > 0) {
          mapped[field.field] = selected
        }
      })
      this.$set(this.excelImportColumnOverrides, sheet, {
        ...existing,
        ...mapped,
      })
      this.excelResolutionModalOpen = false
      this.runExcelImport({ previewOnly: false })
    },
    previewTargetKey(target, idx) {
      return String(target?.sheet || target?.category || target?.menu || idx)
    },
    previewFieldOptions(target, field) {
      const columns = Array.isArray(target?.columns) ? target.columns : []
      const options = columns.map(col => ({
        value: Number(col.index),
        text: `${col.letter || ''} - ${col.label || ''}`,
      }))
      const unresolved = Array.isArray(target?.unresolved_fields) ? target.unresolved_fields : []
      const unresolvedField = unresolved.find(item => String(item?.field || '') === String(field))
      const candidates = Array.isArray(unresolvedField?.candidates) ? unresolvedField.candidates.map(v => Number(v)) : []
      if (!candidates.length) return options
      return options.filter(opt => candidates.includes(Number(opt.value)))
    },
    previewFieldLabel(field) {
      const labels = {
        ssz: 'Sorszám',
        item_text: 'Tétel szöveg',
        qty: 'Mennyiség',
        unit: 'Egység',
        material_unit_price: 'Anyag egységár',
        base_fee_unit: 'Díj egységár',
      }
      return labels[field] || field
    },
    requiredFieldsForTarget(target) {
      const category = String(target?.category || '')
      if (category === 'jarulekos-koltsegek') {
        return ['item_text', 'qty', 'unit', 'base_fee_unit']
      }
      return ['item_text', 'qty', 'unit', 'material_unit_price', 'base_fee_unit']
    },
    guessColumnForField(target, field) {
      const columns = Array.isArray(target?.columns) ? target.columns : []
      if (!columns.length) return 0

      const normalize = value => String(value || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()

      const keywordMap = {
        ssz: ['ssz', 'sorszam'],
        item_text: ['tetel', 'megnevezes', 'munkanem'],
        qty: ['menny', 'mennyiseg'],
        unit: ['egyseg', 'egys'],
        material_unit_price: ['anyag', 'egysegar', 'egysegar'],
        base_fee_unit: ['dij', 'egysegre', 'egysegar', 'alap dij'],
      }

      const keywords = keywordMap[field] || []
      if (!keywords.length) return 0

      let best = 0
      let bestScore = -1

      columns.forEach(col => {
        const label = normalize(col?.label)
        if (!label) return
        let score = 0
        keywords.forEach(token => {
          if (label.includes(token)) score += token.length
        })
        if (field === 'item_text' && (label.includes('ossz') || label.includes('netto'))) {
          score -= 4
        }
        if ((field === 'material_unit_price' || field === 'base_fee_unit') && label.includes('osszesen')) {
          score -= 8
        }
        if (score > bestScore) {
          bestScore = score
          best = Number(col?.index || 0)
        }
      })

      return bestScore > 0 ? best : 0
    },
    previewResolvedRows(target) {
      const resolved = target?.resolved_fields || {}
      const columns = Array.isArray(target?.columns) ? target.columns : []
      const labels = {
        ssz: 'Sorszám',
        item_text: 'Tétel szöveg',
        qty: 'Mennyiség',
        unit: 'Egység',
        material_unit_price: 'Anyag egységár',
        base_fee_unit: 'Díj egységár',
        material_total: 'Anyag összesen',
        fee_total: 'Díj összesen',
      }
      return Object.keys(labels)
        .filter(key => Number.isFinite(Number(resolved[key])) && Number(resolved[key]) > 0)
        .map(key => {
          const colIndex = Number(resolved[key])
          const col = columns.find(c => Number(c?.index) === colIndex)
          const colText = col ? `${col.letter || ''} - ${col.label || ''}` : `#${colIndex}`
          return {
            label: labels[key],
            column: colText,
          }
        })
    },
    hydratePreviewSelections() {
      const selections = {}
      this.previewTargets.forEach((target, idx) => {
        const key = this.previewTargetKey(target, idx)
        const required = this.requiredFieldsForTarget(target)
        const resolved = target?.resolved_fields || {}
        const unresolved = Array.isArray(target?.unresolved_fields) ? target.unresolved_fields : []
        const rowSelections = {}
        required.forEach(field => {
          const resolvedValue = Number(resolved?.[field] || 0)
          if (Number.isFinite(resolvedValue) && resolvedValue > 0) {
            rowSelections[field] = resolvedValue
            return
          }

          const unresolvedField = unresolved.find(item => String(item?.field || '') === String(field))
          const candidates = Array.isArray(unresolvedField?.candidates) ? unresolvedField.candidates : []
          const firstCandidate = Number(candidates[0] || 0)
          if (Number.isFinite(firstCandidate) && firstCandidate > 0) {
            rowSelections[field] = firstCandidate
            return
          }

          const guessed = Number(this.guessColumnForField(target, field) || 0)
          if (Number.isFinite(guessed) && guessed > 0) {
            rowSelections[field] = guessed
          }
        })
        selections[key] = rowSelections
      })
      this.excelPreviewSelections = selections
    },
    confirmExcelPreview() {
      const nextOverrides = { ...(this.excelImportColumnOverrides || {}) }
      this.previewTargets.forEach((target, idx) => {
        const sheet = String(target?.sheet || '')
        if (!sheet) return
        const key = this.previewTargetKey(target, idx)
        const required = this.requiredFieldsForTarget(target)
        if (!required.length) return
        const selectedMap = this.excelPreviewSelections[key] || {}
        const mapped = {}
        required.forEach(field => {
          const selected = Number(selectedMap[field] || 0)
          if (selected > 0) mapped[field] = selected
        })
        nextOverrides[sheet] = {
          ...(nextOverrides[sheet] || {}),
          ...mapped,
        }
      })
      this.excelImportColumnOverrides = nextOverrides
      this.excelPreviewModalOpen = false
      this.runExcelImport({ previewOnly: false })
    },
    fieldOptions(field) {
      const all = this.resolutionFieldOptions
      const candidates = Array.isArray(field?.candidates) ? field.candidates.map(v => Number(v)) : []
      if (!candidates.length) return all
      return all.filter(opt => candidates.includes(Number(opt.value)))
    },
    openClearModal() {
      if (!this.canManageImportClear) return
      this.clearModalOpen = true
    },
    resetClearDraft() {
      this.clearing = false
      this.clearEntries = true
      this.clearMenu = false
    },
    confirmClearOfferData() {
      if (!this.canManageImportClear) return
      const offerType = String(this.offerType || '')
      const offerId = Number(this.offerId || 0)
      if (!offerType || !offerId) return
      if (!this.clearEntries && !this.clearMenu) return

      this.clearing = true
      axios({
        method: 'post',
        url: 'budget/offer/clear',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        data: {
          offer_type: offerType,
          offer_id: offerId,
          clear_entries: this.clearEntries,
          clear_menu: this.clearMenu,
        },
      }).then(() => {
        this.notify('Kártya adatok törlése kész', 'success', 'CheckIcon')
        this.clearModalOpen = false
        if (this.clearMenu && this.$store) {
          this.$store.commit('app/SET_PREP_BUDGET_CUSTOM_MENUS', [])
          this.$store.commit('app/SET_PREP_BUDGET_ACTIVE_MENU', '')
        }
        this.fetchItems(false)
      }).catch(() => {
        this.notify('Kártya adatok törlése sikertelen', 'danger', 'XOctagonIcon')
      }).finally(() => {
        this.clearing = false
      })
    },
    resolvedCategoryKey() {
      if (this.categoryKey) return this.categoryKey
      if (this.endpoint.includes('zsaluzas-allvanyozas')) return 'zsaluzas-allvanyozas'
      if (this.endpoint.includes('sikalapozas')) return 'sikalapozas'
      if (this.endpoint.includes('mely-alapozas')) return 'melyalapozas'
      if (this.endpoint.includes('/alapozas')) return 'alapozas'
      if (this.endpoint.includes('helyszini-beton-vasbeton')) return 'helyszini-beton-vasbeton'
      if (this.endpoint.includes('egyeb')) return 'egyeb'
      if (this.endpoint.includes('osszesito')) return 'osszesito'
      return ''
    },
    updateRow(change) {
      this.correctionBaseRows = null
      const row = this.rows.find(item => Number(item.id) === Number(change.item_id))
      if (!row) return
      row.qty = change.qty
      row.unit = change.unit
      row.material_unit_price = change.material_unit_price
      row.base_fee_unit = change.base_fee_unit
      row.planned_expense_scope = change.planned_expense_scope
      const qty = change.qty !== null && change.qty !== undefined && change.qty !== '' ? Number(change.qty) : null
      const mUnit = change.material_unit_price !== null && change.material_unit_price !== undefined && change.material_unit_price !== '' ? Number(change.material_unit_price) : null
      const dUnit = change.base_fee_unit !== null && change.base_fee_unit !== undefined && change.base_fee_unit !== '' ? Number(change.base_fee_unit) : null
      row.material_total = (qty !== null && mUnit !== null) ? Number((qty * mUnit).toFixed(2)) : null
      row.fee_total = (qty !== null && dUnit !== null) ? Number((qty * dUnit).toFixed(2)) : null
      this.hasChanges = true
      this.scheduleAutoPersist()
    },
    scheduleAutoPersist() {
      if (this.isSummaryCategory) return
      if (this.autosaveTimer) {
        clearTimeout(this.autosaveTimer)
      }
      const entriesSnapshot = this.rows.map(row => ({
        item_id: row.id,
        qty: row.qty,
        unit: row.unit,
        material_unit_price: row.material_unit_price,
        base_fee_unit: row.base_fee_unit,
        planned_expense_scope: row.planned_expense_scope || 'none',
      }))
      const context = {
        category: this.resolvedCategoryKey(),
        offerType: String(this.offerType || ''),
        offerId: Number(this.offerId || 0),
      }
      this.pendingAutosaveContext = context
      this.pendingAutosaveEntries = entriesSnapshot
      this.autosaveTimer = setTimeout(() => {
        this.autosaveTimer = null
        this.persistEntriesSilent(this.pendingAutosaveContext, this.pendingAutosaveEntries)
        this.pendingAutosaveContext = null
        this.pendingAutosaveEntries = null
      }, 500)
    },
    flushAutoPersist() {
      if (!this.autosaveTimer) return
      clearTimeout(this.autosaveTimer)
      this.autosaveTimer = null
      this.persistEntriesSilent(this.pendingAutosaveContext, this.pendingAutosaveEntries)
      this.pendingAutosaveContext = null
      this.pendingAutosaveEntries = null
    },
    fetchItems(showToast = false) {
      this.loading = true
      this.error = ''
      this.rows = []
      this.correctionBaseRows = null
      const category = this.resolvedCategoryKey()
      const offerType = String(this.offerType || '')
      const offerId = Number(this.offerId || 0)

      if (!category || !offerType || !offerId) {
        this.rows = []
        this.error = 'Hiányzó költségvetés azonosító.'
        this.loading = false
        return
      }

      axios({
        method: 'get',
        url: 'budget/offer/entries',
        params: {
          offer_type: offerType,
          offer_id: offerId,
          category,
          subgroup_key: this.subgroupKey || '',
        },
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      }).then(response => {
        const payload = response.data || {}
        const items = Array.isArray(payload)
          ? payload
          : (Array.isArray(payload.rows) ? payload.rows : [])
        const hasEntries = Array.isArray(payload)
          ? false
          : !!payload.has_entries
        this.rows = items.map(item => ({
          id: item.id,
          itemText: item.item_text || '',
          qty: item.qty ?? null,
          unit: item.unit || '',
          material_unit_price: item.material_unit_price ?? null,
          base_fee_unit: item.base_fee_unit ?? null,
          material_total: item.material_total ?? null,
          fee_total: item.fee_total ?? null,
          planned_expense_scope: item.planned_expense_scope || 'none',
        }))
        this.hasPersistedEntries = hasEntries
        this.hasChanges = !hasEntries
        if (showToast) {
          this.notify('Frissítés sikeres', 'success', 'CheckIcon')
        }
      }).catch(() => {
        this.error = 'Nem sikerült betölteni a tételeket.'
        this.rows = []
        if (showToast) {
          this.notify('Frissítés sikertelen', 'danger', 'XOctagonIcon')
        }
      }).finally(() => {
        this.loading = false
      })
    },
    saveItems() {
      if (this.hasPersistedEntries) return
      this.persistEntries('Mentés sikeres', 'Mentés sikertelen')
    },
    refreshItems() {
      if (!this.hasPersistedEntries) return
      this.persistEntries('Frissítés sikeres', 'Frissítés sikertelen')
    },
    persistEntries(successTitle, errorTitle) {
      this.correctionBaseRows = null
      const category = this.resolvedCategoryKey()
      const offerType = String(this.offerType || '')
      const offerId = Number(this.offerId || 0)
      if (!category || !offerType || !offerId) return

      this.saving = true
      axios({
        method: 'post',
        url: 'budget/offer/entries',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        data: {
          offer_type: offerType,
          offer_id: offerId,
          category,
          subgroup_key: this.subgroupKey || '',
          entries: this.rows.map(row => ({
            item_id: row.id,
            qty: row.qty,
            unit: row.unit,
            material_unit_price: row.material_unit_price,
            base_fee_unit: row.base_fee_unit,
            planned_expense_scope: row.planned_expense_scope || 'none',
          })),
        },
      }).then(() => {
        this.hasPersistedEntries = true
        this.hasChanges = false
        this.notify(successTitle, 'success', 'CheckIcon')
        this.fetchItems(false)
      }).catch(() => {
        this.notify(errorTitle, 'danger', 'XOctagonIcon')
      }).finally(() => {
        this.saving = false
      })
    },
    persistEntriesSilent(context, entriesSnapshot = null) {
      this.correctionBaseRows = null
      const category = context?.category || this.resolvedCategoryKey()
      const offerType = context?.offerType || String(this.offerType || '')
      const offerId = context?.offerId || Number(this.offerId || 0)
      if (!category || !offerType || !offerId) return
      if (this.loading || this.saving || this.autosaving || !this.hasChanges) return
      const entries = Array.isArray(entriesSnapshot)
        ? entriesSnapshot
        : this.rows.map(row => ({
          item_id: row.id,
          qty: row.qty,
          unit: row.unit,
          material_unit_price: row.material_unit_price,
          base_fee_unit: row.base_fee_unit,
          planned_expense_scope: row.planned_expense_scope || 'none',
        }))

      this.autosaving = true
      axios({
        method: 'post',
        url: 'budget/offer/entries',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        data: {
          offer_type: offerType,
          offer_id: offerId,
          category,
          subgroup_key: this.subgroupKey || '',
          entries,
        },
      }).then(() => {
        this.hasPersistedEntries = true
        this.hasChanges = false
      }).catch(() => {
        // csendes autosave hiba, kézi Mentés/Frissítés továbbra is elérhető
      }).finally(() => {
        this.autosaving = false
      })
    },
    copyRows(rows) {
      return (rows || []).map(row => ({ ...row }))
    },
    applyNegotiatedAdjustment(rows, correction) {
      const enabled = !!(correction?.negotiatedEnabled ?? correction?.negotiated_enabled)
      if (!enabled) return rows
      const targetNet = Math.round(Number(correction?.negotiatedNet ?? correction?.negotiated_net ?? 0))
      const applyMaterial = !!(correction?.negotiatedMaterial ?? correction?.negotiated_material)
      const applyFee = !!(correction?.negotiatedFee ?? correction?.negotiated_fee)
      if (!Number.isFinite(targetNet) || (!applyMaterial && !applyFee)) return rows

      const slots = []
      rows.forEach((row, idx) => {
        const qty = row.qty !== null && row.qty !== undefined && row.qty !== '' ? Number(row.qty) : null
        if (!Number.isFinite(qty) || qty === 0) return
        if (applyMaterial && row.material_unit_price !== null && row.material_unit_price !== undefined && row.material_unit_price !== '') {
          slots.push({ idx, type: 'material' })
        }
        if (applyFee && row.base_fee_unit !== null && row.base_fee_unit !== undefined && row.base_fee_unit !== '') {
          slots.push({ idx, type: 'fee' })
        }
      })

      if (!slots.length) return rows

      const currentNet = rows.reduce(
        (sum, row) => sum + (Number(row.material_total) || 0) + (Number(row.fee_total) || 0),
        0,
      )
      const delta = Number((targetNet - currentNet).toFixed(2))
      if (Math.abs(delta) < 0.01) return rows

      const nextRows = this.copyRows(rows)
      const getSlotTotal = slot => {
        const row = nextRows[slot.idx]
        return Number(slot.type === 'material' ? (row.material_total || 0) : (row.fee_total || 0))
      }
      const setSlotTotal = (slot, rawTotal) => {
        const row = nextRows[slot.idx]
        const qty = Number(row.qty)
        if (!Number.isFinite(qty) || qty === 0) return false
        const nextTotal = Number(Math.max(0, rawTotal).toFixed(2))
        const nextUnit = Number((nextTotal / qty).toFixed(2))
        if (slot.type === 'material') {
          row.material_total = nextTotal
          row.material_unit_price = nextUnit
        } else {
          row.fee_total = nextTotal
          row.base_fee_unit = nextUnit
        }
        return true
      }

      if (delta > 0) {
        const share = Number((delta / slots.length).toFixed(2))
        slots.forEach(slot => {
          const current = getSlotTotal(slot)
          setSlotTotal(slot, current + share)
        })
      } else {
        let remaining = Number(Math.abs(delta).toFixed(2))
        let active = [...slots]
        while (remaining >= 0.01 && active.length) {
          const share = Number((remaining / active.length).toFixed(2))
          if (share <= 0) break
          const nextActive = []
          active.forEach(slot => {
            const current = getSlotTotal(slot)
            const reduceBy = Math.min(current, share)
            setSlotTotal(slot, current - reduceBy)
            remaining = Number((remaining - reduceBy).toFixed(2))
            if ((current - reduceBy) > 0.009) nextActive.push(slot)
          })
          active = nextActive
        }
      }

      const afterNet = nextRows.reduce(
        (sum, row) => sum + (Number(row.material_total) || 0) + (Number(row.fee_total) || 0),
        0,
      )
      const remainder = Number((targetNet - afterNet).toFixed(2))
      if (Math.abs(remainder) >= 0.01 && slots.length) {
        const first = slots[0]
        const current = getSlotTotal(first)
        const adjusted = current + remainder
        if (adjusted >= 0) {
          setSlotTotal(first, adjusted)
        }
      }

      return nextRows
    },
    applyCorrectionPreview(correction) {
      if (this.loading) return
      if (this.resolvedCategoryKey() === 'jarulekos-koltsegek') return
      if (!this.correctionBaseRows) {
        this.correctionBaseRows = this.copyRows(this.rows)
      }
      const globalPercent = Number(
        correction && typeof correction === 'object'
          ? (correction.globalPercent ?? correction.global_percent ?? 0)
          : (correction || 0),
      )
      const materialPercent = Number(
        correction && typeof correction === 'object'
          ? (correction.materialPercent ?? correction.material_percent ?? 0)
          : 0,
      )
      const feePercent = Number(
        correction && typeof correction === 'object'
          ? (correction.feePercent ?? correction.fee_percent ?? 0)
          : 0,
      )
      const globalMultiplier = 1 + (globalPercent / 100)
      const materialMultiplier = globalMultiplier * (1 + (materialPercent / 100))
      const feeMultiplier = globalMultiplier * (1 + (feePercent / 100))
      const correctedRows = this.correctionBaseRows.map(row => {
        const qty = row.qty !== null && row.qty !== undefined && row.qty !== '' ? Number(row.qty) : null
        const materialUnit = row.material_unit_price !== null && row.material_unit_price !== undefined && row.material_unit_price !== ''
          ? Number(row.material_unit_price)
          : null
        const feeUnit = row.base_fee_unit !== null && row.base_fee_unit !== undefined && row.base_fee_unit !== ''
          ? Number(row.base_fee_unit)
          : null
        const nextMaterialUnit = materialUnit === null ? null : Number((materialUnit * materialMultiplier).toFixed(2))
        const nextFeeUnit = feeUnit === null ? null : Number((feeUnit * feeMultiplier).toFixed(2))
        return {
          ...row,
          material_unit_price: nextMaterialUnit,
          base_fee_unit: nextFeeUnit,
          material_total: (qty !== null && nextMaterialUnit !== null) ? Number((qty * nextMaterialUnit).toFixed(2)) : null,
          fee_total: (qty !== null && nextFeeUnit !== null) ? Number((qty * nextFeeUnit).toFixed(2)) : null,
        }
      })
      this.rows = this.applyNegotiatedAdjustment(correctedRows, correction)
    },
    resetCorrectionPreview() {
      this.fetchItems(false)
    },
  },
}
</script>

<style scoped>
.prep-budget-save {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
  position: sticky;
  top: 0;
  z-index: 6;
  padding: 0.35rem 0.4rem;
  border-radius: 12px;
  background: linear-gradient(180deg, color-mix(in srgb, var(--prep-surface-1) 72%, #0a1020 28%) 0%, transparent 100%);
  backdrop-filter: blur(3px);
}

.prep-action-btn {
  border-radius: 10px !important;
  border-width: 1px !important;
  transition: transform 0.15s ease, box-shadow 0.18s ease, filter 0.18s ease !important;
  backdrop-filter: blur(2px);
}

.prep-action-btn:hover:not(:disabled) {
  transform: translateY(-1px);
}

.prep-action-btn-import,
.prep-action-btn-assign,
.prep-action-btn-new,
.prep-action-btn-refresh,
.prep-action-btn-save {
  color: #e2ecff !important;
  border-color: color-mix(in srgb, var(--prep-accent-base) 45%, #ffffff 10%) !important;
  background-image: linear-gradient(
    120deg,
    color-mix(in srgb, var(--prep-accent-strong) 42%, rgba(15, 23, 42, 0.9) 58%) 0%,
    color-mix(in srgb, var(--prep-accent-base) 40%, rgba(30, 41, 59, 0.95) 60%) 55%,
    color-mix(in srgb, var(--prep-accent-soft) 35%, rgba(30, 41, 59, 0.95) 65%) 100%
  ) !important;
  box-shadow: 0 6px 18px color-mix(in srgb, var(--prep-accent-base) 35%, transparent 65%);
}

.prep-action-btn-import:hover:not(:disabled),
.prep-action-btn-assign:hover:not(:disabled),
.prep-action-btn-new:hover:not(:disabled),
.prep-action-btn-refresh:hover:not(:disabled),
.prep-action-btn-save:hover:not(:disabled) {
  filter: brightness(1.08);
  box-shadow: 0 10px 24px color-mix(in srgb, var(--prep-accent-base) 46%, transparent 54%);
}

.prep-action-btn-clear {
  color: #fff1f2 !important;
  border-color: color-mix(in srgb, var(--prep-danger-base) 42%, #ffffff 12%) !important;
  background-image: linear-gradient(
    118deg,
    color-mix(in srgb, var(--prep-danger-base) 40%, rgba(15, 23, 42, 0.9) 60%) 0%,
    color-mix(in srgb, var(--prep-warning-base) 24%, rgba(30, 41, 59, 0.95) 76%) 100%
  ) !important;
  box-shadow: 0 6px 18px color-mix(in srgb, var(--prep-danger-base) 32%, transparent 68%);
}

.prep-action-btn-clear:hover:not(:disabled) {
  filter: brightness(1.08);
  box-shadow: 0 10px 24px color-mix(in srgb, var(--prep-danger-base) 44%, transparent 56%);
}

.prep-action-btn:disabled {
  opacity: 0.45 !important;
  box-shadow: none !important;
}

.prep-assign-list {
  max-height: 380px;
  overflow: auto;
  border: 1px solid rgba(148, 163, 184, 0.25);
  border-radius: 8px;
  padding: 0.4rem;
  outline: none;
}

.prep-assign-item {
  width: 100%;
  text-align: left;
  border: 1px solid rgba(148, 163, 184, 0.2);
  border-radius: 6px;
  background: rgba(15, 23, 42, 0.72);
  color: #e5e7eb;
  padding: 0.45rem 0.55rem;
  margin-bottom: 0.35rem;
  cursor: pointer;
}

.prep-assign-item.selected {
  border-color: #60a5fa;
  background: rgba(59, 130, 246, 0.2);
}

.prep-budget-summary-bar {
  position: fixed;
  left: 270px;
  right: 24px;
  bottom: 0;
  z-index: 50;
  min-height: 78px;
  border-radius: 12px 12px 0 0;
  background:
    radial-gradient(130% 140% at 4% 0%, color-mix(in srgb, var(--prep-accent-soft) 18%, transparent 82%) 0%, transparent 55%),
    linear-gradient(140deg, color-mix(in srgb, var(--prep-surface-1) 80%, #0b1220 20%) 0%, color-mix(in srgb, var(--prep-surface-2) 80%, #08101f 20%) 100%);
  border: 1px solid color-mix(in srgb, var(--prep-accent-soft) 26%, rgba(148, 163, 184, 0.28) 74%);
  box-shadow: 0 -14px 30px rgba(15, 23, 42, 0.42);
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 2rem;
  padding: 1.1rem 1.2rem;
  color: #e5e7eb;
}

.prep-budget-table-scroll {
  padding-bottom: 122px;
}

.prep-budget-summary-title {
  font-weight: 700;
  margin-right: 0.75rem;
  white-space: nowrap;
  font-size: 1.3rem;
  line-height: 1.1;
  color: #e5e7eb;
}

.prep-budget-summary-items {
  margin-left: auto;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 2rem;
}

.prep-budget-summary-item {
  min-width: 247px;
  border: 1px solid color-mix(in srgb, var(--prep-warning-base) 66%, #fde68a 34%);
  border-radius: 8px;
  background:
    linear-gradient(135deg,
      color-mix(in srgb, var(--prep-warning-base) 80%, #fde047 20%) 0%,
      color-mix(in srgb, var(--prep-accent-soft) 24%, var(--prep-warning-base) 76%) 100%
    );
  padding: 0.45rem;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 0.35rem;
}

.prep-budget-summary-item-label-box {
  border: 1px solid rgba(148, 163, 184, 0.28);
  border-radius: 6px;
  background:
    linear-gradient(120deg,
      color-mix(in srgb, var(--prep-surface-1) 84%, #111 16%) 0%,
      color-mix(in srgb, var(--prep-surface-2) 88%, #111 12%) 100%);
  padding: 0.28rem 0.45rem;
}

.prep-budget-summary-item-label {
  display: block;
  font-size: 0.95rem;
  color: #e5e7eb;
  font-weight: 700;
  white-space: normal;
}

.prep-budget-summary-item-value-box {
  border: 1px solid color-mix(in srgb, var(--prep-danger-base) 35%, rgba(148, 163, 184, 0.35) 65%);
  border-radius: 6px;
  background:
    linear-gradient(140deg,
      color-mix(in srgb, var(--prep-surface-2) 76%, #111 24%) 0%,
      color-mix(in srgb, var(--prep-danger-base) 12%, #111 88%) 100%);
  padding: 0.3rem 0.45rem;
}

.prep-budget-summary-item-value {
  display: block;
  color: color-mix(in srgb, var(--prep-danger-base) 80%, #fecaca 20%);
  line-height: 1.1;
  white-space: nowrap;
}

.prep-budget-summary-item.total {
  border-color: color-mix(in srgb, var(--prep-accent-base) 48%, var(--prep-warning-base) 52%);
  background:
    linear-gradient(130deg,
      color-mix(in srgb, var(--prep-warning-base) 64%, #fde047 36%) 0%,
      color-mix(in srgb, var(--prep-accent-base) 36%, var(--prep-warning-base) 64%) 100%);
}

@media (max-width: 1199px) {
  .prep-budget-summary-bar {
    left: 14px;
    right: 14px;
    bottom: 0;
    gap: 1rem 2rem;
    min-height: 78px;
    padding: 0.95rem;
    border-radius: 12px 12px 0 0;
  }

  .prep-budget-summary-item {
    width: 100%;
    min-width: 0;
  }

  .prep-budget-summary-items {
    width: 100%;
    margin-left: 0;
    justify-content: flex-start;
    flex-direction: column;
    align-items: stretch;
    gap: 1rem;
  }

  .prep-budget-summary-item-label {
    white-space: nowrap;
  }
}
</style>
